import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setToken, tokenSelector } from '@redux/auth';
import { getUserInfo } from '@redux/auth/thunks';
import { clearNotifications, newNotifyCountSelector } from '@redux/notifications';
import { getNewNotifyCount } from '@redux/notifications/thunks';
import { useBodyScrollLock, useMount, useRouter } from '@hooks';
import { clearUserInfo } from '@redux/user';
import { Link } from 'react-router-dom';
import { getCookie, onPopoverClick, setCookie } from '@utils';
import { useOutsideClick } from '@hooks/useOutsideCLick';
import { usePopoverSlide } from '@hooks/usePopoverSlide';
import { PATHS } from '@constants';
import ConditionalRenderer, { Else, If } from '@components/ConditionalRenderer';
import Modal from '@components/Modal';
import Icon from '@components/Icon';
import Button from '@components/Button';
import Burger from '@components/Burger';
import Logo from '@components/Logo';
import HeaderMenu from '@components/HeaderMenu';
import NotifyBadge from '@components/NotifyBadge';
import './styles.scss';
import { toast } from '@components/Toast';
import { isIOS, isMacOs } from 'react-device-detect';

const Header = () => {
  const { lockScroll, unlockScroll } = useBodyScrollLock();

  const [isNavVisible, setIsNavVisible] = useState(false);
  const [isAccountDropdownVisible, setIsAccountDropdownVisible] = useState(false);
  const [isProfileDropdownVisible, setIsProfileDropdownVisible] = useState(false);
  const [isCookieModalVisible, setIsCookieModalVisible] = useState(false);

  const dispatch = useDispatch();
  const { push } = useRouter();
  const profileRef = useRef(null);
  const profilePopoverRef = useRef(null);
  const accountPopoverRef = useRef(null);

  useOutsideClick(profileRef, () => onPopoverCloseClick(setIsProfileDropdownVisible));
  usePopoverSlide(profilePopoverRef, () => onPopoverCloseClick(setIsProfileDropdownVisible));
  usePopoverSlide(accountPopoverRef, () => onPopoverCloseClick(setIsAccountDropdownVisible));

  const token = useSelector(tokenSelector);
  const newNotifyCount = useSelector(newNotifyCountSelector);

  const onPopoverTriggerClick = (callback) => {
    callback((prevState) => {
      if (window.innerWidth <= 1100) {
        if (!prevState) {
          lockScroll();
        } else {
          unlockScroll();
        }
      }

      return !prevState;
    });
  };

  const onPopoverCloseClick = (callback) => {
    callback(false);
    unlockScroll();
  };

  const closeCookieModal = () => {
    setIsCookieModalVisible(false);
    unlockScroll();
  };

  const clearState = () => {
    dispatch(clearNotifications());
    dispatch(clearUserInfo());
    dispatch(setToken(''));
    push(PATHS.SIGN_IN);
  };

  const onLogoutClick = () => {
    clearState();
  };

  useEffect(() => {
    if (token) {
      dispatch(getNewNotifyCount());
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      dispatch(getUserInfo());
    }
  }, [token]);

  useMount(() => {
    if (!getCookie('cookieModalShown')) {
      setCookie('cookieModalShown', true, 7);
      setIsCookieModalVisible(true);
    }

    if (!getCookie('mobileAppShown')) {
      setCookie('mobileAppShown', true, 1);
      setTimeout(() => {
        let appLink = 'https://play.google.com/store/apps/details?id=com.thevann.vann&pcampaignid=web_share';

        if (isIOS || isMacOs) {
          appLink = 'https://apps.apple.com/us/app/vann-by-cancer-survivor/id1663619147';
        }

        toast.info({
          title: 'Vann is better on the app ',
          duration: 6000,
          link: appLink,
        });
      }, 3000);
    }
  });

  return (
    <>
      <header className='header'>
        <div className='row'>
          <div className='column sm-12'>
            <div className='header__wrapper'>
              <div className={`header__nav header-nav md-order-2 ${isNavVisible ? 'active' : ''}`}>
                <div className='header-nav__burger'>
                  <Burger isNavVisible={isNavVisible} onClick={() => onPopoverTriggerClick(setIsNavVisible)} />
                </div>
                <HeaderMenu />
              </div>
              <div className='header__logo md-order-1'>
                <Logo />
              </div>
              <ConditionalRenderer condition={!token}>
                <If>
                  <div
                    className={`header__account popover-container popover-container_tablet header-account md-order-3 ${isAccountDropdownVisible ? 'open' : ''}`}
                  >
                    <div className='header-account__trigger popover-trigger' onClick={() => onPopoverTriggerClick(setIsAccountDropdownVisible)}>
                      <Icon type='user' />
                    </div>
                    <div
                      id='account-dropdown'
                      className='header-account__btns popover-wrap'
                      onClick={(e) => onPopoverClick(e, () => onPopoverCloseClick(setIsAccountDropdownVisible))}
                    >
                      <div className='header-account__btns-wrap popover' ref={accountPopoverRef}>
                        <Link to={PATHS.SIGN_IN} className='label-lg tablet-label-lg mobile-label-lg'>Login</Link>
                        <Button
                          title='Create Account'
                          size='sm'
                          theme='primary'
                          linkTo={PATHS.SIGN_UP}
                          className='field-text tablet-field-text mobile-field-text'
                        />
                      </div>
                    </div>
                  </div>
                </If>
                <Else>
                  <div
                    className={`header__profile header-profile popover-container popover-container_tablet md-order-3 ${isProfileDropdownVisible ? 'open' : ''}`}
                    ref={profileRef}
                  >
                    <div className='header-profile__notify'>
                      <NotifyBadge newNotifyCount={newNotifyCount} />
                    </div>
                    <div
                      className='header-profile__trigger popover-trigger label-lg tablet-label-lg mobile-label-lg'
                      onClick={() => onPopoverTriggerClick(setIsProfileDropdownVisible)}
                    >
                      <Icon type='profile' />
                      <Icon type='user' />
                      <span>My Profile</span>
                    </div>
                    <div
                      className='header-profile__btns popover-wrap'
                      onClick={(e) => onPopoverClick(e, () => onPopoverCloseClick(setIsProfileDropdownVisible))}
                    >
                      <div className='header-profile__btns-wrap popover' ref={profilePopoverRef}>
                        <Link to={PATHS.PROFILE} className='header-profile__btns-link label-lg tablet-label-lg mobile-label-lg'>
                          <Icon type='user' />
                          <span>My Profile</span>
                        </Link>
                        <div className='header-profile__btns-link label-lg tablet-label-lg mobile-label-lg' onClick={onLogoutClick}>
                          <Icon type='log-out' />
                          <span>Sign Out</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Else>
              </ConditionalRenderer>
            </div>
          </div>
        </div>
      </header>
      <Modal
        isVisible={isCookieModalVisible}
        closeModal={closeCookieModal}
        classNames='cookie-modal center-modal'
        size='xs'
        title='We use cookies'
        desc='We collect strictly necessary cookies to deliver the best experience on our website. By using our website, you agree to the use of cookies. '
        button={<Button
          theme='primary'
          className='field-text tablet-field-text mobile-field-text'
          title='Accept'
          onClick={closeCookieModal}
        />
        }
      />
    </>
  );
};

export default Header;
